import React, { Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, Checkbox, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { useAuthContext } from 'AuthContext';
import { useMeetingHubContext } from '../../MeetingHubContext';
import reducer, {
  AttendanceTableAction,
  AttendanceTableActionType,
  AttendanceTableState,
  initialState,
} from './AttendanceTableReducer';
import MeetingService from 'services/MeetingService';
import Table from 'components/shared/Table';
import RemoveAttendeeModal from './RemoveAttendeeModal';
import { Attendee } from 'store/types/Meeting';
import { SortConfig, TableColumn } from 'store/types/Table';
import { getFormattedDate } from 'util/format';
import { defaultSnackbarErrorProps } from 'util/layout';

import styles from './AttendanceTable.module.scss';

interface InterestVoterPermissions {
  isPresidingChairAttendee: boolean;
  canToggle: boolean;
  canRequest: boolean;
}

const defaultPermissions: InterestVoterPermissions = {
  canToggle: false,
  canRequest: false,
  isPresidingChairAttendee: false,
};

const AttendanceTable: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { profile, isStaff } = useAuthContext();
  const {
    meetingId,
    attendeeId,
    meetingSession,
    attendees,
    loadingAttendees,
    isPresidingChair,
    callHubMethod,
    refetchAttendees,
  } = useMeetingHubContext();
  const [{ list = [], sort }, dispatch] = useReducer<Reducer<AttendanceTableState, AttendanceTableAction>>(
    reducer,
    initialState
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [removeAttendeeModalOpen, setRemoveAttendeeModalOpen] = useState<boolean>(false);
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | undefined>(undefined);

  const affiliateHasVotingMember = useMemo(() => {
    const map = new Map<string, string>();

    list.forEach((attendee) => {
      if (attendee.isVotingMember && attendee.affiliate) {
        map.set(attendee.affiliate, attendee.id);
      }
    });

    return map;
  }, [list]);

  const interestVoterPermissions = useMemo(() => {
    return list.reduce<Record<string, InterestVoterPermissions>>((acc, record) => {
      const isSelf = record.id === attendeeId;
      const isSemiAttendee = record.affiliate?.toLowerCase() === 'semi';
      const isPresidingChairAttendee = meetingSession?.presidingChairId === record.id;
      const sameAffiliate = record.affiliate === profile?.affiliate.affiliateName;
      const affiliateOwnerId = affiliateHasVotingMember.get(record.affiliate ?? '');
      const affiliateAlreadyTaken = affiliateOwnerId !== undefined && affiliateOwnerId !== record.id;

      const canToggle =
        !isSemiAttendee &&
        (sameAffiliate || isStaff) &&
        !isPresidingChairAttendee &&
        (isStaff || (!affiliateAlreadyTaken && (!record.isVotingMember || isSelf)));

      const canRequest =
        !isStaff &&
        !isPresidingChair &&
        record.id !== attendeeId &&
        record.affiliate === profile?.affiliate.affiliateName &&
        record.isVotingMember;

      acc[record.id] = {
        isPresidingChairAttendee,
        canToggle,
        canRequest,
      };

      return acc;
    }, {});
  }, [
    list,
    affiliateHasVotingMember,
    attendeeId,
    meetingSession?.presidingChairId,
    isStaff,
    isPresidingChair,
    profile?.affiliate.affiliateName,
  ]);

  useEffect(() => {
    if (attendees) {
      dispatch({
        type: AttendanceTableActionType.SetInitialList,
        payload: { initialList: attendees },
      });
    }
  }, [attendees]);

  const onSortChange = useCallback((newSort: SortConfig) => {
    dispatch({ type: AttendanceTableActionType.UpdateSort, payload: { sort: newSort } });
  }, []);

  const handleCheckboxClick = useCallback(
    (attendee: Attendee) => () => {
      setSaving(true);

      const updatedAttendee = {
        ...attendee,
        isVotingMember: !attendee.isVotingMember,
      };

      MeetingService.setVotingMemberAsync(updatedAttendee)
        .then(() => {
          refetchAttendees?.();
          enqueueSnackbar(`Updated interest voter`, { variant: 'success' });
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to update interest voter`, defaultSnackbarErrorProps);
          console.error('Error updating attendee:', error);
        })
        .finally(() => setSaving(false));
    },
    [enqueueSnackbar, refetchAttendees]
  );

  const handleDeleteAttendeeModalOpen = useCallback((attendee: Attendee) => {
    setSelectedAttendee(attendee);
    setRemoveAttendeeModalOpen(true);
  }, []);

  const handleDeleteAttendeeModalClose = useCallback(() => {
    setRemoveAttendeeModalOpen(false);
    setSelectedAttendee(undefined);
  }, []);

  const handleDeleteAttendeeModalSubmit = useCallback(() => {
    setRemoveAttendeeModalOpen(false);
    setSelectedAttendee(undefined);
  }, []);

  const requestInterestVoter = useCallback(
    (attendee: Attendee) => {
      callHubMethod('RequestVotingRole', {
        meetingId,
        requesterAttendeeId: attendeeId,
        targetAttendeeId: attendee.id,
      });
    },
    [attendeeId, callHubMethod, meetingId]
  );

  const sendCallReminder = useCallback(
    (attendee: Attendee) => {
      callHubMethod('SendCallReminder', {
        meetingId,
        targetAttendeeId: attendee.id,
      });
    },
    [callHubMethod, meetingId]
  );

  const columns: Array<TableColumn<Attendee>> = useMemo(() => {
    const result: Array<TableColumn<Attendee>> = [
      {
        dataIndex: 'isVotingMember',
        label: <Typography fontWeight="bold">Interest Voter</Typography>,
        sortable: true,
        render: (_, record) => {
          const { isPresidingChairAttendee, canToggle, canRequest } =
            interestVoterPermissions[record.id] ?? defaultPermissions;

          return (
            <>
              <Checkbox
                color={'primary'}
                checked={record.isVotingMember}
                onChange={handleCheckboxClick(record)}
                disabled={saving || loadingAttendees || !canToggle}
              />
              {isPresidingChairAttendee && <StarIcon fontSize="small" />}
              {canRequest && (
                <Button color="primary" variant="contained" onClick={() => requestInterestVoter(record)}>
                  Request
                </Button>
              )}
            </>
          );
        },
      },
      {
        dataIndex: 'name',
        label: <Typography fontWeight="bold">Name</Typography>,
        sortable: true,
        render: (_, record) => <Typography>{record.name}</Typography>,
      },
      {
        dataIndex: 'company',
        label: <Typography fontWeight="bold">Company</Typography>,
        sortable: true,
        render: (_, record) => <Typography>{record.company}</Typography>,
      },
      {
        dataIndex: 'affiliate',
        label: <Typography fontWeight="bold">Voting Interest</Typography>,
        sortable: true,
        render: (_, record) => <Typography>{record.affiliate}</Typography>,
      },
      {
        dataIndex: 'joinedAt',
        label: <Typography fontWeight="bold">Join Date</Typography>,
        sortable: true,
        render: (_, record) => <Typography>{getFormattedDate(record.joinedAt ?? '', 'L LT')}</Typography>,
      },
    ];

    if (isStaff) {
      result.push({
        key: 'actions',
        align: 'center',
        label: <Typography fontWeight="bold">Actions</Typography>,
        render: (_, record) => {
          return (
            <div className={styles.actions}>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                onClick={() => sendCallReminder(record)}
                disabled={saving || record.id === attendeeId}
              >
                Message
              </Button>
              <IconButton color="error" onClick={() => handleDeleteAttendeeModalOpen(record)} disabled={saving}>
                <CloseIcon className={styles.closeButton} />
              </IconButton>
            </div>
          );
        },
      });
    }

    return result;
  }, [
    isStaff,
    interestVoterPermissions,
    handleCheckboxClick,
    saving,
    loadingAttendees,
    requestInterestVoter,
    sendCallReminder,
    attendeeId,
    handleDeleteAttendeeModalOpen,
  ]);

  return (
    <>
      <Table
        className={styles.table}
        columns={columns}
        list={list}
        sort={sort}
        onSortChange={onSortChange}
        loading={loadingAttendees}
        noResultsText="No current attendance"
      />
      <RemoveAttendeeModal
        data={selectedAttendee}
        open={removeAttendeeModalOpen}
        onClose={handleDeleteAttendeeModalClose}
        onSubmit={handleDeleteAttendeeModalSubmit}
      />
    </>
  );
};

export default AttendanceTable;
